/**
 * The Settings password actions — ONE copy, for both shells (#346 / #568).
 *
 * There were two, byte-identical, in `App.tsx` and `MobileShell.tsx`, and both
 * carried the same defect:
 *
 *   const result = await authClient.setPassword({ newPassword })
 *   if (result.error) {
 *     const changeResult = await authClient.changePassword({ currentPassword: '', newPassword })
 *     if (changeResult.error) return { error: result.error.message }
 *   }
 *
 * Three things wrong at once: the form never collected a current password; the
 * fallback could never succeed (an empty string does not verify, so it was dead
 * code standing in for a field that was never built); and the handler reported
 * the FIRST call's error, so a user who came to change their password was told
 * "user already has a password" and never saw why the retry failed.
 *
 * Both shells now import these, so there is no second copy to fix next time.
 */
import { useCallback, useEffect, useState } from 'react'
import { getApiBase, sessionFetch } from '@papan/api-client'
import { announceSessionRotated, whileRotatingSession } from '@papan/auth/session-rotation'

/**
 * Whether the caller's account has a password.
 *
 * Returns `null` when the answer is not available — a network failure, or a
 * server that predates the endpoint during a rolling restart. `null` means
 * UNKNOWN and the form must not guess: rendering the set-a-first-password shape
 * for someone who has one is the bug being fixed.
 */
export async function fetchHasPassword(): Promise<boolean | null> {
  try {
    const res = await sessionFetch(`${getApiBase()}/api/auth/has-password`, { credentials: 'include' })
    if (!res.ok) return null
    const body = await res.json() as { hasPassword?: unknown }
    return typeof body.hasPassword === 'boolean' ? body.hasPassword : null
  } catch {
    return null
  }
}

/**
 * Server error → something a person can act on.
 *
 * Better Auth's `message` is developer-facing. Showing it verbatim is how a user
 * trying to CHANGE their password was told "user already has a password" — which
 * reads as "this was unnecessary" when it is the thing they came to do (#568).
 *
 * EVERY CODE HERE WAS MEASURED against the running API, not read off a type.
 * The first cut of this function had three dead arms and shipped the exact
 * string its own docblock claimed to remove:
 *
 *   - `SESSION_EXPIRED` is not a code better-call ever produces. It derives
 *     `code` by upper-casing the MESSAGE, so `BASE_ERROR_CODES.SESSION_EXPIRED`
 *     ("Session expired. Re-authenticate to perform this action.") arrives as
 *     `SESSION_EXPIRED_REAUTHENTICATE_TO_PERFORM_THIS_ACTION` — and only
 *     `deleteUser` throws it, so neither password route can produce it at all.
 *   - the `status === 401` arm could not fire: `sensitiveSessionMiddleware`
 *     throws with an EMPTY body, `authFetch` did `res.json()` on it, and the
 *     resulting parse error arrived with no status. Fixed in `authFetch`.
 *   - nothing mapped on `/set-password`, because our own Hono route flattened
 *     the error and dropped `code`. Fixed at the route.
 *
 * UNKNOWN CODES STILL FALL THROUGH to the raw message on purpose. Replacing
 * everything with a friendly generic would hide the one error that explains a
 * novel failure, and the next person debugging it would have nothing.
 */
function friendlyError(err: { message: string; code?: string; status?: number }): string {
  switch (err.code) {
    case 'INVALID_PASSWORD':
      return 'That current password is not correct. Try again, or use “Forgot your current password?” below the field.'
    case 'PASSWORD_TOO_SHORT':
      return 'That password is too short. Use at least 8 characters.'
    case 'PASSWORD_TOO_LONG':
      return 'That password is too long.'
    case 'USER_ALREADY_HAS_A_PASSWORD':
      // Reachable in the shipped build through a STALE `hasPassword`: the
      // desktop shell keeps decks mounted, so two tabs open, set a password in
      // one, submit in the other, and the second still believes there is none.
      // Left unmapped, that path renders #568's original message on the fixed
      // branch.
      return 'You already have a password. Reopen this page to change it instead.'
    case 'CREDENTIAL_ACCOUNT_NOT_FOUND':
      // The EXACT MIRROR of the arm above — stale `hasPassword === true` rather
      // than stale `false` — and the first cut of this enumeration added one and
      // missed the other. Same cause: the desktop shell keeps decks mounted, so
      // a second tab still believes a password exists after it was removed.
      return 'You do not have a password yet. Reopen this page to set one instead.'
    case 'SESSION_EXPIRED_REAUTHENTICATE_TO_PERFORM_THIS_ACTION':
      return 'For your security, sign in again before changing your password.'
    case 'INVALID_ORIGIN':
    case 'MISSING_OR_NULL_ORIGIN':
    case 'origin_not_trusted':
      // Reachable on a real deployment, not only by curl — though NOT by the
      // mechanism an earlier version of this comment claimed. `CORS_ORIGIN=*`
      // does not make the response readable: the CORS middleware filters a bare
      // `*` out of both its exact set and its suffix list, so no
      // Access-Control-Allow-Origin is sent and the browser blocks the response
      // before this function sees it.
      //
      // The real path is a CF Pages PREVIEW build. `apps/api/src/index.ts`
      // supports `*.foo.pages.dev` suffixes, while `computeApiTrustedOrigins`
      // runs every entry through `safeOrigin()`, where `new URL('*.foo.pages.dev')`
      // throws and the entry is dropped. So a preview origin is CORS-allowed —
      // the response IS readable — and `requireSameOrigin` refuses it. "Invalid
      // origin" then tells the user nothing they can act on and reads like their
      // input was wrong.
      return 'This page could not be verified. Reload it and try again.'
    default:
      // No session at all — the middleware refuses with a 401 before either
      // route runs. Saying "not correct" here would send someone round the reset
      // loop for a password that is fine.
      if (err.status === 401) return 'Your session has expired. Sign in again to continue.'
      if (err.status === 403) return 'This page could not be verified. Reload it and try again.'
      if (err.status === 429) return 'Too many attempts. Wait a minute and try again.'
      return err.message
  }
}

/** First password, for an account that has none (Google-only, or email-code). */
export async function setPassword(newPassword: string): Promise<{ error?: string }> {
  const { authClient } = await import('@papan/auth/client')
  const result = await authClient.setPassword({ newPassword })
  if (result.error) return { error: friendlyError(result.error) }
  return {}
}

/**
 * Change an existing password, proving the current one.
 *
 * `revokeOtherSessions: true` is deliberate and is the re-auth half of #346.
 * Better Auth deletes every session and then immediately re-issues one for THIS
 * device (`update-user.mjs`), so it does not sign the user out — it evicts their
 * other devices and keeps the one they are on. That is what makes this the right
 * default rather than a checkbox:
 *
 *   - the costs are asymmetric. Getting it wrong in the compromise case is
 *     unrecoverable — the user changes their password believing they have locked
 *     the door, and the intruder's session keeps working. Getting it wrong in the
 *     hygiene case costs a re-login on their phone.
 *   - password RESET already evicts everything (`revokeSessionsOnPasswordReset`,
 *     R34 #174). A user should not have to know which of two levers does it.
 *   - a checkbox asks for a threat-model judgement at the moment the user is
 *     least equipped to make one, and an unticked default leaves the dangerous
 *     case unprotected.
 *
 * ── IT IS IMMEDIATE, AND THAT IS WHY THE COPY MAY SAY SO ──────────────────
 *
 * Deleting the other sessions' DB ROWS alone could not reach a cookie already in
 * someone else's browser: `session.cookieCache` answers from a signed 5-minute
 * cookie without reading the table, so an evicted device kept working until it
 * expired (#601, measured). The server now moves the account's session marker
 * (`users.sessions_valid_from`) before the rows go, and every session reader —
 * our routes, Better Auth's own endpoints, WS tokens, preview and WOPI tokens —
 * refuses anything issued before it; open sockets are closed. Password RESET does
 * the same. `shared/auth/src/session-revocation-hooks.ts` is where it happens.
 *
 * So the success copy says "have been signed out". A user changing their password
 * because they suspect a compromise reads exactly that line — it may only claim
 * what the server enforced before answering.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ error?: string }> {
  const { authClient } = await import('@papan/auth/client')
  // Revokes this browser's session too and re-issues it in the response, so the
  // session-death detector must not read the in-between 401s as a sign-out — and
  // afterwards, what the old session minted (open previews) is re-minted (#601).
  const result = await whileRotatingSession(() => authClient.changePassword({
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  }))
  if (!result.error) announceSessionRotated()
  // The error that ACTUALLY applies — not another call's — and in words the
  // person can act on.
  if (result.error) return { error: friendlyError(result.error) }
  return {}
}

/**
 * Everything the Settings password form needs, so neither shell assembles it.
 *
 * The refresh after a successful SET is not decoration: the desktop shell keeps
 * decks mounted for the life of the tab (CLAUDE.md — "a page whose data arrives
 * from a `useEffect(…, [])` fetches ONCE, EVER"), so without it a user who sets
 * their first password would still be offered the set-a-first-password form
 * afterwards, and their next attempt would fail with the very error this change
 * exists to remove.
 */
export function usePasswordActions(): {
  hasPassword: boolean | null
  onSetPassword: (newPassword: string) => Promise<{ error?: string }>
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<{ error?: string }>
  onRetryHasPassword: () => void
} {
  const [hasPassword, setHasPassword] = useState<boolean | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let alive = true
    void fetchHasPassword().then(v => { if (alive) setHasPassword(v) })
    return () => { alive = false }
  }, [attempt])

  /**
   * Re-ask. Without this, ONE failed fetch at boot latches `null` for the life
   * of the browser tab on desktop — the shell keeps decks mounted, the effect
   * has no other trigger, and Settings → Password renders permanently disabled
   * reading "Loading…" with no error and no way out but a full reload. Mobile
   * escapes it only because its settings screen is a real route that remounts.
   */
  const onRetryHasPassword = useCallback(() => {
    setHasPassword(null)
    setAttempt(a => a + 1)
  }, [])

  const onSetPassword = useCallback(async (newPassword: string) => {
    const result = await setPassword(newPassword)
    if (!result.error) setHasPassword(true)
    return result
  }, [])

  const onChangePassword = useCallback(
    (currentPassword: string, newPassword: string) => changePassword(currentPassword, newPassword),
    [],
  )

  return { hasPassword, onSetPassword, onChangePassword, onRetryHasPassword }
}
